import React, { useEffect } from 'react'
import type { SessionStatus } from '../types'

interface HelpModalProps {
  isOpen: boolean
  onClose: () => void
  sessionStatus?: SessionStatus
}

const HelpModal: React.FC<HelpModalProps> = ({ isOpen, onClose, sessionStatus }) => {
  // Handle keyboard shortcut to open/close modal
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === '?' && !event.ctrlKey && !event.altKey && !event.metaKey) {
        // Only trigger if not in an input field
        const target = event.target as HTMLElement
        if (target.tagName !== 'INPUT' && target.tagName !== 'TEXTAREA' && !target.isContentEditable) {
          event.preventDefault()
          if (isOpen) {
            onClose()
          } else {
            // This would need to be handled by parent component
          }
        }
      }
      
      if (event.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    document.addEventListener('keydown', handleKeyPress)
    return () => document.removeEventListener('keydown', handleKeyPress)
  }, [isOpen, onClose])

  if (!isOpen) return null

  const getSessionStatusDisplay = () => {
    if (!sessionStatus) return null

    switch (sessionStatus.status) {
      case 'active':
        return (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
            <div className="flex items-center">
              <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse mr-2"></div>
              <span className="text-sm font-medium text-green-800">
                Session Active ({sessionStatus.screenshotCount || 0} screenshots)
              </span>
            </div>
            <p className="text-xs text-green-700 mt-1">
              Use CTRL+SHIFT+6 to add more screenshots or CTRL+SHIFT+7 to submit.
            </p>
          </div>
        )
      case 'processing':
        return (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
            <div className="flex items-center">
              <div className="w-2 h-2 rounded-full bg-blue-400 mr-2"></div>
              <span className="text-sm font-medium text-blue-800">
                Session Processing
              </span>
            </div>
            <p className="text-xs text-blue-700 mt-1">
              Your session is being analyzed. Results will appear shortly.
            </p>
          </div>
        )
      default:
        return (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
            <div className="flex items-center">
              <div className="w-2 h-2 rounded-full bg-gray-400 mr-2"></div>
              <span className="text-sm font-medium text-gray-800">
                No Active Session
              </span>
            </div>
            <p className="text-xs text-gray-700 mt-1">
              Use CTRL+SHIFT+5 to start a new session or CTRL+SHIFT+8 for a single screenshot.
            </p>
          </div>
        )
    }
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              Keyboard Shortcuts
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Current Session Status */}
          {getSessionStatusDisplay()}

          {/* Shortcuts List */}
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-3">
                Screenshot Capture
              </h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <kbd className="px-2 py-1 bg-gray-200 rounded text-sm font-mono">
                      CTRL+SHIFT+8
                    </kbd>
                    <span className="text-sm text-gray-700">Single screenshot</span>
                  </div>
                  <span className="text-xs text-gray-500">Quick capture & analyze</span>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-3">
                Multi-Screenshot Sessions
              </h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between py-2 px-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <kbd className="px-2 py-1 bg-blue-200 rounded text-sm font-mono">
                      CTRL+SHIFT+5
                    </kbd>
                    <span className="text-sm text-gray-700">Start session</span>
                  </div>
                  <span className="text-xs text-gray-500">Begin multi-screenshot capture</span>
                </div>
                
                <div className="flex items-center justify-between py-2 px-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <kbd className="px-2 py-1 bg-blue-200 rounded text-sm font-mono">
                      CTRL+SHIFT+6
                    </kbd>
                    <span className="text-sm text-gray-700">Add screenshot to session</span>
                  </div>
                  <span className="text-xs text-gray-500">Capture additional images</span>
                </div>
                
                <div className="flex items-center justify-between py-2 px-3 bg-green-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <kbd className="px-2 py-1 bg-green-200 rounded text-sm font-mono">
                      CTRL+SHIFT+7
                    </kbd>
                    <span className="text-sm text-gray-700">Submit session</span>
                  </div>
                  <span className="text-xs text-gray-500">Analyze all screenshots together</span>
                </div>
                
                <div className="flex items-center justify-between py-2 px-3 bg-red-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <kbd className="px-2 py-1 bg-red-200 rounded text-sm font-mono">
                      CTRL+SHIFT+4
                    </kbd>
                    <span className="text-sm text-gray-700">Cancel session</span>
                  </div>
                  <span className="text-xs text-gray-500">Discard current session</span>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-3">
                Web Portal
              </h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <kbd className="px-2 py-1 bg-gray-200 rounded text-sm font-mono">
                      ?
                    </kbd>
                    <span className="text-sm text-gray-700">Show/hide this help</span>
                  </div>
                  <span className="text-xs text-gray-500">Toggle keyboard shortcuts</span>
                </div>
                
                <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <kbd className="px-2 py-1 bg-gray-200 rounded text-sm font-mono">
                      ESC
                    </kbd>
                    <span className="text-sm text-gray-700">Close modal</span>
                  </div>
                  <span className="text-xs text-gray-500">Close dialogs and modals</span>
                </div>
              </div>
            </div>
          </div>

          {/* Usage Tips */}
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="text-sm font-medium text-blue-900 mb-2">
              💡 Usage Tips
            </h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Use sessions for long documents, multi-step processes, or complex interfaces</li>
              <li>• Single screenshots work best for quick questions or simple captures</li>
              <li>• Sessions can contain up to 20 screenshots and auto-expire after 30 minutes</li>
              <li>• Enable Live Sync to see real-time updates as you capture screenshots</li>
            </ul>
          </div>

          {/* Close Button */}
          <div className="flex justify-end mt-6">
            <button
              onClick={onClose}
              className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default HelpModal